// =====================================================================
// Assessment Center — Instructor Panel
// Grading Review page — read the essay answer (plus any attached
// images/PDFs), score it, write feedback, and — once every essay on
// the attempt is reviewed — publish the final grade. Multiple choice
// answers are shown read-only for context (already graded
// automatically at submission).
// =====================================================================
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, Field, Input, Modal, PageHeader, Spinner, Textarea } from "../../../shared/components/ui";
import {
  getAttemptForReview,
  publishGrade,
  submitEssayReview,
  type AttemptForReview,
  type PublishGradeResult,
  type ReviewAttachment,
  type ReviewQuestion,
} from "../lib/grading-api";

function AttachmentList({ attachments }: { attachments: ReviewAttachment[] }) {
  const [preview, setPreview] = useState<ReviewAttachment | null>(null);
  if (attachments.length === 0) return null;

  return (
    <div>
      <p className="mb-2 text-sm font-medium text-navy-900">Attachments</p>
      <div className="flex flex-wrap gap-3">
        {attachments.map((a) =>
          a.file_type === "image" ? (
            <button
              key={a.id}
              type="button"
              onClick={() => setPreview(a)}
              className="group relative overflow-hidden rounded-lg border border-border-soft"
            >
              <img src={a.file_url} alt={a.file_name} className="h-24 w-32 object-cover" />
              <span className="absolute inset-0 hidden items-center justify-center bg-navy-900/40 text-xs font-medium text-white group-hover:flex">
                Open image
              </span>
            </button>
          ) : (
            <a
              key={a.id}
              href={a.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-48 items-center gap-2 rounded-lg border border-border-soft bg-white px-3 py-2 text-sm text-navy-700 hover:bg-navy-50"
            >
              <span aria-hidden>📄</span>
              <span className="truncate">{a.file_name}</span>
              <span className="ml-auto text-xs text-slate-400">↗</span>
            </a>
          )
        )}
      </div>

      <Modal open={!!preview} onClose={() => setPreview(null)} title={preview?.file_name ?? "Attachment"} widthClass="max-w-3xl">
        {preview && <img src={preview.file_url} alt={preview.file_name} className="w-full rounded-lg" />}
      </Modal>
    </div>
  );
}

function EssayReviewForm({
  question,
  onSaved,
}: {
  question: ReviewQuestion;
  onSaved: () => void;
}) {
  const [score, setScore] = useState<number | "">(question.existing_score ?? "");
  const [feedback, setFeedback] = useState(question.existing_feedback ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const completed = question.review_status === "completed";

  async function handleSave() {
    if (score === "" || !question.review_id) {
      setError("Enter a score before saving.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await submitEssayReview({
        reviewId: question.review_id,
        score: Number(score),
        feedback,
        pointsPossible: question.points_possible,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save this review.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {question.grading_rubric && (
        <div className="rounded-lg bg-navy-50/60 px-3 py-2 text-sm text-navy-800">
          <span className="font-medium">Rubric: </span>
          {question.grading_rubric}
        </div>
      )}
      {question.model_answer && (
        <div className="rounded-lg border border-dashed border-border-soft px-3 py-2 text-sm text-slate-500">
          <span className="font-medium text-slate-600">Model answer: </span>
          {question.model_answer}
        </div>
      )}

      <div>
        <p className="mb-1.5 text-sm font-medium text-navy-900">Student's answer</p>
        <p className="whitespace-pre-wrap rounded-lg border border-border-soft bg-white px-3 py-2 text-sm text-slate-700">
          {question.student_answer_text || <span className="italic text-slate-400">No answer submitted</span>}
        </p>
      </div>

      <AttachmentList attachments={question.attachments ?? []} />

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-[140px_1fr] sm:items-start">
        <Field label={`Score (out of ${question.points_possible})`}>
          <Input
            type="number"
            min={0}
            max={question.points_possible}
            step={0.5}
            value={score}
            disabled={completed}
            onChange={(e) => setScore(e.target.value === "" ? "" : Number(e.target.value))}
          />
        </Field>
        <Field label="Feedback">
          <Textarea value={feedback} disabled={completed} onChange={(e) => setFeedback(e.target.value)} />
        </Field>
      </div>

      <div className="flex items-center gap-3">
        <Button disabled={completed || saving} onClick={handleSave}>
          {saving ? <Spinner /> : null} {completed ? "Review saved" : "Save review"}
        </Button>
        {completed && <Badge tone="green">Completed</Badge>}
      </div>
    </div>
  );
}

export default function GradingReviewPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AttemptForReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishedGrade, setPublishedGrade] = useState<PublishGradeResult | null>(null);

  function load() {
    if (!attemptId) return;
    setLoading(true);
    getAttemptForReview(attemptId).then((d) => {
      setData(d);
      setLoading(false);
    });
  }

  useEffect(load, [attemptId]);

  async function handlePublish() {
    if (!attemptId) return;
    setPublishError(null);
    setPublishing(true);
    try {
      const result = await publishGrade(attemptId);
      setPublishedGrade(result);
      load();
    } catch (e) {
      setPublishError(e instanceof Error ? e.message : "Couldn't publish this grade.");
    } finally {
      setPublishing(false);
    }
  }

  if (loading || !data) {
    return (
      <div className="flex items-center gap-2 py-20 text-slate-500">
        <Spinner /> Loading submission…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Review Submission"
        subtitle={`${data.exam_title} · Attempt #${data.attempt_number} · Student ${data.student_id}`}
      />

      {data.already_published && (
        <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          This grade has been published — the student can see their final result.
          {publishedGrade && (
            <span className="ml-1 font-medium">
              ({publishedGrade.total_score}/{publishedGrade.max_score} · {publishedGrade.passed ? "Passed" : "Not passed"})
            </span>
          )}
        </div>
      )}

      <div className="space-y-6">
        {data.sections.map((section) => (
          <Card key={section.id}>
            <div className="border-b border-border-soft px-5 py-4">
              <h3 className="font-display text-base font-semibold text-navy-900">{section.title}</h3>
            </div>
            <ul className="divide-y divide-border-soft">
              {section.questions.map((q, i) => (
                <li key={q.exam_question_id} className="px-5 py-4">
                  <p className="mb-3 text-sm font-medium text-navy-900">
                    {i + 1}. {q.prompt}
                  </p>

                  {q.type === "multiple_choice" ? (
                    <div className="flex flex-wrap items-center gap-3 rounded-lg bg-navy-50/60 px-3 py-2 text-sm">
                      <span className="text-slate-600">
                        Selected: {q.selected_option_text ?? <span className="italic text-slate-400">no answer</span>}
                      </span>
                      <Badge tone={q.is_correct ? "green" : "red"}>
                        {q.is_correct ? "Correct" : "Incorrect"} · {q.points_awarded ?? 0}/{q.points_possible} pts
                      </Badge>
                    </div>
                  ) : (
                    <EssayReviewForm question={q} onSaved={load} />
                  )}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      {!data.already_published && (
        <Card className="mt-6 flex flex-wrap items-center justify-between gap-3 p-5">
          <div>
            <p className="font-display text-base font-semibold text-navy-900">Publish grade</p>
            <p className="text-sm text-slate-500">
              {data.ready_to_publish
                ? "Every essay on this attempt has been reviewed — publishing releases the final result to the student."
                : "Save a review for every essay answer above before you can publish."}
            </p>
            {publishError && <p className="mt-1 text-sm text-red-600">{publishError}</p>}
          </div>
          <Button disabled={!data.ready_to_publish || publishing} onClick={handlePublish}>
            {publishing ? <Spinner /> : null} Publish grade
          </Button>
        </Card>
      )}

      <div className="mt-6 flex justify-between">
        <Link to="/instructor/assessment-center/grading" className="text-sm font-medium text-navy-700 hover:underline">
          ← Back to Grading
        </Link>
        <Button variant="secondary" onClick={() => navigate("/instructor/assessment-center/grading")}>
          Done
        </Button>
      </div>
    </div>
  );
}
